import { PagedList, SearchSmsItemResponse } from 'core'
import SmsDirection from 'core/domain/sms/smsDirection'
import SmsStatus from 'core/domain/sms/smsStatus'

import { Button, Card, Col, Icon, Row, Skeleton, Tag, Tooltip } from 'common/components'
import { formatDateTime } from 'common/dateFormatters'

import TableV2 from '../dataDisplay/TableV2'

import './styles.less'

interface SmsTableProps {
  messages?: PagedList<SearchSmsItemResponse>
  loadingSms: boolean
  onChange?: (filters: { pageNumber: number; pageSize: number; orderColumn: string; orderDescending: boolean }) => any
}

const SmsTable: React.FC<SmsTableProps> = ({ messages, loadingSms, onChange }) => {
  const messsageColumns: any[] = [
    {
      title: 'Date',
      dataIndex: 'sendDate',
      key: 'sendDate',
      sorter: false,
      render: (sendDate: string) => <span>{formatDateTime(sendDate)}</span>,
    },
    {
      title: 'First name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: false,
    },
    {
      title: 'Last name',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: false,
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      sorter: false,
      render: (direction: SmsDirection) => <SmsDirectionTag direction={direction} />,
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      sorter: false,
    },
    {
      title: 'Subject',
      dataIndex: 'messageBody',
      key: 'messageBody',
      sorter: false,
      render: (messageBody: string) => (
        <Tooltip title={''} overlay={messageBody}>
          {messageBody.substr(0, 30)}
          {messageBody.length > 30 && '...'}
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: false,
      render: (status: SmsStatus) => <SmsStatusTag status={status} />,
    },
  ]

  const onMessagesTableChange = (pagination: any, _: any, sorter: any) => {
    if (onChange) {
      onChange({
        pageNumber: pagination.current,
        pageSize: pagination.pageSize,
        orderColumn: sorter.field || 'sendDate',
        orderDescending: sorter.order !== 'ascend',
      })
    }
  }

  return (
    messages && (
      <TableV2
        className={'sms-table'}
        dataSource={messages.items}
        onChange={onMessagesTableChange}
        loading={loadingSms}
        scroll={{ x: 'max-content' }}
        columns={messsageColumns}
        pagination={{
          total: messages?.totalItemCount,
          pageSize: messages?.pageSize,
          current: messages?.pageNumber,
          showSizeChanger: true,
          pageSizeOptions: ['25', '50', '80', '100'],
        }}
      />
    )
  )
}

type SmsStatusTagProps = {
  status: SmsStatus
}

const SmsStatusTag = (props: SmsStatusTagProps) => {
  switch (props.status) {
    case SmsStatus.Delivered:
      return (
        <Tooltip title={'Delivered'} overlay={<>This message was successfully sent and delivered to the user.</>}>
          <Tag color={'blue'}>
            <i className="fa fa-paper-plane"></i>
            <span className="sms-status">Delivered</span>
          </Tag>
        </Tooltip>
      )
    case SmsStatus.Undelivered:
      return (
        <Tooltip
          title={'Undelivered'}
          overlay={<>This message was successfully sent but not successfully delivered to the user.</>}
        >
          <Tag color={'yellow'}>
            <i className="far fa-info-circle"></i>
            <span className="sms-status">Undelivered</span>
          </Tag>
        </Tooltip>
      )
    case SmsStatus.Failed:
      return (
        <Tooltip title={'Failed'} overlay={<>This message failed to send.</>}>
          <Tag color={'red'}>
            <i className="fa fa-times-circle"></i>
            <span className="sms-status">Failed</span>
          </Tag>
        </Tooltip>
      )
    case SmsStatus.Received:
      return (
        <Tooltip title={'Received'} overlay={<>This message was received by PeopleKeep from the user.</>}>
          <Tag color={'green'}>
            <i className="fa fa-check-circle"></i>
            <span className="sms-status">Received</span>
          </Tag>
        </Tooltip>
      )
    default:
      return null
  }
}

type SmsDirectionTagProps = {
  direction: SmsDirection
}

const SmsDirectionTag = (props: SmsDirectionTagProps) => {
  switch (props.direction) {
    case SmsDirection.Outbound:
      return <span>Outgoing</span>
    case SmsDirection.Inbound:
      return <span>Incoming</span>
    default:
      return null
  }
}

export default SmsTable
